import {Box, Typography} from '@viptech/react-components'
import {ReactNode, useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import Loading from 'src/components/Loading'
import {CountingFacesDTO} from 'src/hooks/api/personalized-dashboard/useGetTotalFromCountingFaces'
import useWebsocket from 'src/hooks/api/socket/useWebsocket'
import VideoPlayer from 'src/pages/camera-config/components/camera-general-data/VideoPlayer'
import {LayoutTypes} from 'src/pages/camera-mosaic'
import FacesCountingEventList from 'src/pages/camera-mosaic/components/FacesCountingEventList'
import PlayerBar from 'src/pages/camera-mosaic/components/PlayerBar'

import api, {Camera} from 'src/services/api'
import {FaceDetectionWSChannelEnum, WebSocketRoomTypeEnum} from 'src/utilities/enums/WSChannelEnums'

export type defaultProps = {
  cameras: Camera[]
  layoutType: number
  showBarriers: boolean
}

export type StreamsAsync = {tag: 'Empty'} | {tag: 'Loading'; data: Camera} | {tag: 'Success'; data: Camera; hash: string} | {tag: 'Error'; data: Camera}

const sizes = [1, 2, 4, 9, 16] as const

function LayoutDefault(props: defaultProps) {
  const {cameras, layoutType, showBarriers} = props

  function getMosaicSize(cameraCount: number) {
    return sizes.find((size) => size >= Math.max(cameraCount, 1)) ?? sizes[sizes.length - 1]
  }

  function getGridColumns(mosaicSize: number) {
    switch (mosaicSize) {
      case 16:
        return 4
      case 9:
        return 3
      case 4:
        return 2
      default:
        return 1
    }
  }

  const cameraCount = cameras.length
  const mosaicSize = getMosaicSize(cameraCount || 1)
  const gridColumns = getGridColumns(mosaicSize)
  const gridRows = cameraCount > 0 ? Math.ceil(cameraCount / gridColumns) : 1

  const [streams, setStreams] = useState<StreamsAsync[]>([{tag: 'Empty'}])

  const navigate = useNavigate()

  function handleKeyPress(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (document.fullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        }
      } else {
        navigate(-1)
      }
    }
  }

  function handleFullscreenChange() {
    if (!document.fullscreenElement) {
      navigate(-1)
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress)
    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const [offsetStream, setOffsetStream] = useState<number>()
  const [playbackSpeeedRate, setPlaybackSpeeedRate] = useState<number>(1)
  const [streamErrorMap, setStreamErrorMap] = useState<Record<number, boolean>>({})
  const loadRequestIdRef = useRef(0)

  useEffect(() => {
    if (!cameras.length) {
      setStreams([{tag: 'Empty'}])
      return
    }

    const requestId = ++loadRequestIdRef.current
    setStreams(cameras.map((camera) => ({tag: 'Loading', data: camera})))

    async function loadConnection() {
      const nextStreams: StreamsAsync[] = cameras.map((camera) => ({tag: 'Loading', data: camera}))

      await Promise.all(
        cameras.map(async (camera, index) => {
          try {
            const stream =
              offsetStream !== undefined
                ? api.streaming.startStreamWithCameraId(camera.id, offsetStream)
                : api.streaming.startStream((await api.streaming.getStream(camera.id)).data.data)
            nextStreams[index] = {tag: 'Success', data: camera, hash: stream}
          } catch {
            nextStreams[index] = {tag: 'Error', data: camera}
          }
        }),
      )

      if (loadRequestIdRef.current === requestId) {
        setStreams(nextStreams)
      }
    }

    loadConnection()
  }, [cameras, offsetStream])

  useEffect(() => {
    setStreamErrorMap({})
  }, [cameras, offsetStream])

  const [socketCounting, setSocketCounting] = useState<CountingFacesDTO>()

  const onMessage = (counting: CountingFacesDTO) => {
    setSocketCounting(counting)
  }

  useWebsocket({
    room: WebSocketRoomTypeEnum.COUNTING,
    listenTo: [FaceDetectionWSChannelEnum.COUTING],
    onMessage,
    enable: layoutType === LayoutTypes.Facial,
  })

  function getStreamKey(streamAsync: StreamsAsync) {
    if (streamAsync.tag === 'Empty') return 'empty'
    return `${streamAsync.data.id}-${streamAsync.tag}`
  }

  function renderCameraLabel(camera: Camera) {
    return (
      <Box padding='5px'>
        <Typography variant='p' color='white' children={`#(${camera.id}) ${camera.name}`} />
      </Box>
    )
  }

  function renderTileMedia(children: ReactNode) {
    return (
      <Box width='100%' flex={1} minHeight='0' display='flex' direction='column' align='center' justifyContent='center' rowGap='8px' backgroundColor='#1a1a1a'>
        {children}
      </Box>
    )
  }

  function renderTileContent(streamAsync: StreamsAsync) {
    if (streamAsync.tag === 'Empty') {
      return (
        <Box width='100%' height='100%' display='flex' direction='column' minHeight='0'>
          {renderTileMedia(<Typography variant='p' color='white' children='Selecione câmeras ao lado para montar o layout' />)}
        </Box>
      )
    }

    if (streamAsync.tag === 'Loading') {
      return (
        <Box width='100%' height='100%' display='flex' direction='column' minHeight='0'>
          {renderCameraLabel(streamAsync.data)}
          {renderTileMedia(
            <>
              <Loading width='48px' height='48px' color='#ffffff' />
              <Typography variant='p' color='white' size='12px' children='Conectando ao stream...' />
            </>,
          )}
        </Box>
      )
    }

    if (streamAsync.tag === 'Error') {
      return (
        <Box width='100%' height='100%' display='flex' direction='column' minHeight='0'>
          {renderCameraLabel(streamAsync.data)}
          {renderTileMedia(
            <>
              <Typography variant='p' color='white' children='Não foi possível iniciar o stream' />
              <Typography variant='p' color='#98A2B3' size='12px' children='Verifique se a câmera está online e tente novamente' />
            </>,
          )}
        </Box>
      )
    }

    const hasPlaybackError = streamErrorMap[streamAsync.data.id]

    return (
      <Box width='100%' height='100%' display='flex' direction='column' minHeight='0'>
        {renderCameraLabel(streamAsync.data)}
        {renderTileMedia(
          hasPlaybackError ? (
            <>
              <Typography variant='p' color='white' children='Não foi possível reproduzir o vídeo' />
              <Typography variant='p' color='#98A2B3' size='12px' children='O stream foi aberto, mas a reprodução falhou' />
            </>
          ) : (
            <Box width='100%' height='100%' position='relative'>
              <VideoPlayer
                src={streamAsync.hash}
                height='100%'
                cameraId={streamAsync.data.id}
                drawBarrierCoordinates={showBarriers}
                playbackSpeeedRate={playbackSpeeedRate}
                setIsErrorOnStream={(isError) => {
                  setStreamErrorMap((prev) => ({
                    ...prev,
                    [streamAsync.data.id]: isError,
                  }))
                }}
              />
            </Box>
          ),
        )}
      </Box>
    )
  }

  return (
    <Box display='flex' direction='column' height='100%' minHeight='0' justifyContent='space-between'>
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${gridRows}, minmax(0, 1fr))`,
          gap: '4px',
        }}>
        {streams.map((streamAsync) => (
          <Box display='flex' minHeight='0' height='100%' key={getStreamKey(streamAsync)}>
            <Box
              display='flex'
              align='center'
              justifyContent='center'
              width='100%'
              minHeight='0'
              backgroundColor='#000000'
              border='2px solid #3E3E3E'
              style={{flex: layoutType === LayoutTypes.Facial && streamAsync.tag === 'Success' ? undefined : 1}}>
              {renderTileContent(streamAsync)}
            </Box>

            {streamAsync.tag === 'Success' && !streamErrorMap[streamAsync.data.id] && layoutType === LayoutTypes.Facial && (
              <Box height='100%'>
                <FacesCountingEventList
                  mosaico={mosaicSize}
                  camera={streamAsync.data.id}
                  companyId={streamAsync.data.companyId}
                  socketCounting={socketCounting}
                />
              </Box>
            )}
          </Box>
        ))}
      </Box>
      {cameras.length > 0 && (
        <Box style={{flexShrink: 0}}>
          <PlayerBar
            setOffset={setOffsetStream}
            offset={offsetStream}
            setPlaybackSpeeedRate={setPlaybackSpeeedRate}
            playbackSpeeedRate={playbackSpeeedRate}
            cameraIds={cameras.map((it) => it.id)}
          />
        </Box>
      )}
    </Box>
  )
}

export default LayoutDefault
